import type { BaseSyntheticEvent, FormHTMLAttributes } from 'react'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { forwardRef } from '#src/utils/forwardRef.ts'
import { mergeRefs } from '#src/utils/mergeRefs.ts'

export interface FormProps extends Omit<
  FormHTMLAttributes<HTMLFormElement>,
  'onSubmit'
> {
  onSubmit?: (e?: BaseSyntheticEvent) => any
  disableSubmitOnMetaEnter?: boolean
}

export const Form = forwardRef<HTMLFormElement, FormProps>(
  ({ onSubmit, disableSubmitOnMetaEnter, ...delegated }, ref) => {
    const submitHotkeyRef = useHotkeys(
      Hotkeys.SubmitForm,
      () => {
        if (window.document.activeElement instanceof HTMLElement) {
          window.document.activeElement.blur()
        }
        onSubmit?.()
      },
      {
        enableOnContentEditable: true,
        enableOnFormTags: ['input', 'select', 'textarea'],
        preventDefault: true,
        enabled: !disableSubmitOnMetaEnter,
      }
    )

    return (
      <form
        ref={mergeRefs([ref, submitHotkeyRef])}
        // React's synthetic event tree bubbles a portal'd form's submit up
        // through its ancestor form, even though the DOM trees are separate.
        // Without this, a nested Form (e.g. inside a dialog) auto-submits
        // its parent form when its own submit fires.
        onSubmit={(e) => {
          e.stopPropagation()
          onSubmit?.(e)
        }}
        {...delegated}
      />
    )
  }
)
